import { useMemo } from 'react';

import { monorepoRoutePaths, routePaths as adminRoutePaths, routePaths } from '@apps/admin/route.paths';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { EventPageSlugs } from '@apps/admin/routes/Dashboard/common/enums/EventPageSlugs';
import { ProductVisibilityEnum } from '@apps/admin/routes/Dashboard/common/enums/ProductVisibilityEnum';
import { useRouterHelper, useTranslation } from '@shared/core';
import {
  Calendar,
  Design,
  EditPages,
  Faq,
  Guests,
  Home,
  Photos,
  Registry,
  Settings,
  Story,
  Plane,
  Video,
  Bed,
  CustomPage,
  Add,
  WebGlobe,
  Experiences,
} from '@withjoy/joykit/icons';

import { ProductVerticals } from '@shared/core/productVerticals/productVerticals.types';
import { ProductRow } from '../../PrimaryDashboardContent/PrimaryDashboardContent.types';
import { ProductRowSubItem } from '../../PrimaryDashboardContent/ProductRowItem';
import { PageInfo } from './useProductRows';
import { EventDesignPurpose, EventPageType, useGetWebsiteDesignerVisitStatusQuery } from '@graphql/generated';
import { useAddCustomPageDialogContext } from '@shared/context/AddCustomPageDialog/AddCustomPageDialog.provider';
import { DesignConsoleRouteName } from '@apps/admin/routes/WebsiteDesignerV2/components/DesignConsole/DesignConsoleRouter/DesignConsoleRouter.types';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useThingsToDoPage } from '@website-editor/utils/ThingsToDo.util';
import { useWebsiteDesignerV3Feature } from '@apps/admin/routes/WebsiteDesignerV2/components/EditorV3/hooks/useWebsiteDesignerV3Feature';
import { useMigratedCustomPageFeatureFlags } from '@shared/hooks/useMigratedCustomPageFeatureFlags/useMigratedCustomPageFeatureFlags';
import { shouldUseNativePageEntry } from '@shared/utils/migratedCustomPages';
import { useIsAdminDashboardUpdateEnabled } from '@apps/admin/routes/Dashboard/hooks/useIsAdminDashboardUpdateEnabled';
import { useEventInfo } from '@shared/utils/eventInfo';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';

function getWebsiteEnablementAndVisibility(eventPages: ReadonlyArray<PageInfo>, productVerticals: ProductVerticals) {
  const websiteSectionPages = eventPages.filter(({ pageSlug }) => pageSlug !== EventPageSlugs.Registry && pageSlug !== EventPageSlugs.Rsvp && pageSlug !== EventPageSlugs.Guests);
  let areAllPagesPrivate = true;
  let areAllPagesDisabled = true;

  websiteSectionPages.forEach(page => {
    if (page.visibility !== 'hidden') {
      areAllPagesDisabled = false;

      if (page.visibility !== 'passwordProtected' && page.pageSlug !== EventPageSlugs.Welcome) {
        areAllPagesPrivate = false;
      }
    }
  });

  return {
    isEnabled: !productVerticals.optOutOfWebsite,
    visibility: areAllPagesDisabled ? ProductVisibilityEnum.Hidden : areAllPagesPrivate ? ProductVisibilityEnum.Private : ProductVisibilityEnum.Public,
  };
}

export const useWebsiteProductRow = (eventPages: ReadonlyArray<PageInfo>, productVerticals: ProductVerticals): ProductRow => {
  const routeHelpers = useRouterHelper();
  const telemetry = useDashboardTelemetry();
  // tq is the new quick translation function
  const { tq } = useTranslation('adminDashboardV2');
  const { openAddCustomPageDialog, setCustomPageDialogData } = useAddCustomPageDialogContext();
  const useCarbonScopedWebsiteEditor = useProFeature(EventFeature.useCarbonScopedWebsiteEditor);

  const { eventHandle } = useEventInfo();

  const [isGalleryV2Enabled] = useIsFeatureEnabled('websiteDesignerTemplatesGalleryV2Enabled');
  const { value: isWebsiteDesignerV3Enabled } = useWebsiteDesignerV3Feature();
  const { isMigratedCustomPageEnabled } = useMigratedCustomPageFeatureFlags();

  const isAdminDashboardUpdateEnabled = useIsAdminDashboardUpdateEnabled();

  const { data: websiteDesignerVisitStatus, loading: isLoadingVisitStatus } = useGetWebsiteDesignerVisitStatusQuery({
    variables: { eventHandle: eventHandle || '', purpose: EventDesignPurpose.live },
    batchMode: 'fast',
    skip: !eventHandle || !isAdminDashboardUpdateEnabled,
  });

  const { isThingsToDoPageEditorSeen, isThingsToDoSlug } = useThingsToDoPage(eventPages);

  const websiteProductRow = useMemo<ProductRow>(() => {
    const { isEnabled, visibility } = getWebsiteEnablementAndVisibility(eventPages, productVerticals);
    const pagesDisabled = new Set<string>();
    eventPages.forEach(
      ({ pageSlug, visibility, type, id }) =>
        visibility === 'hidden' && pagesDisabled.add(type === EventPageType.custom && pageSlug !== EventPageSlugs.Accommodations ? id : pageSlug),
    );
    const pageSlugToTitleMap = eventPages.reduce<Record<string, string>>((acc, page) => {
      if (page.pageSlug && page.pageTitle) {
        acc[page.pageSlug] = page.pageTitle;
      }
      return acc;
    }, {});

    let editPagesSubItems: ProductRowSubItem[] = [];
    const editItemsDictionary = {
      [EventPageSlugs.Welcome]: [
        {
          title: tq`Home`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Welcome],
          icon: Home,
          path: routeHelpers.buildPath(monorepoRoutePaths.welcome),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'welcome', section: 'Website', categoryArg }),
          isDisabled: pagesDisabled.has(EventPageSlugs.Welcome),
          telemetryName: monorepoRoutePaths.welcome,
        },
        {
          title: tq`Video`,
          icon: Video,
          path: routeHelpers.buildPath(monorepoRoutePaths.video),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'video', section: 'Website', categoryArg }),
          isDisabled: pagesDisabled.has(EventPageSlugs.Video),
          telemetryName: monorepoRoutePaths.video,
        },
      ],
      [EventPageSlugs.Story]: [
        {
          title: tq`Story & Tidbits`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Story],
          icon: Story,
          path: routeHelpers.buildPath(monorepoRoutePaths.story),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'story', section: 'Website', categoryArg }),
          isDisabled: pagesDisabled.has(EventPageSlugs.Story),
          telemetryName: monorepoRoutePaths.story,
        },
      ],
      [EventPageSlugs.Schedule]: [
        {
          title: tq`Schedule`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Schedule],
          icon: Calendar,
          path: routeHelpers.buildPath(monorepoRoutePaths.schedule),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'schedule', section: 'Website', categoryArg }),
          isDisabled: pagesDisabled.has(EventPageSlugs.Schedule),
          telemetryName: monorepoRoutePaths.schedule,
        },
      ],
      [EventPageSlugs.Travel]: [
        {
          title: tq`Travel`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Travel],
          icon: Plane,
          path: routeHelpers.buildPath(monorepoRoutePaths.travel),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'travel', section: 'Website', categoryArg }),
          isDisabled: pagesDisabled.has(EventPageSlugs.Travel),
          telemetryName: monorepoRoutePaths.travel,
        },
      ],
      [EventPageSlugs.Accommodations]: [
        {
          title: tq`Where to Stay`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Accommodations],
          icon: Bed,
          path: routeHelpers.buildPath(EventPageSlugs.Accommodations),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'adminAccommodations', section: 'Website', categoryArg }),
          hasNewPill: true,
          pathIsInJoyWeb: true,
          isDisabled: pagesDisabled.has(EventPageSlugs.Accommodations),
          telemetryName: EventPageSlugs.Accommodations,
        },
      ],
      [EventPageSlugs.Faq]: [
        {
          title: tq`Guest Q&A`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Faq],
          icon: Faq,
          path: routeHelpers.buildPath(monorepoRoutePaths.faq),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'faq', section: 'Website', categoryArg }),
          isDisabled: pagesDisabled.has(EventPageSlugs.Faq),
          telemetryName: monorepoRoutePaths.faq,
        },
      ],
      [EventPageSlugs.WeddingParty]: [
        {
          title: tq`Wedding Party`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.WeddingParty],
          icon: Guests,
          path: routeHelpers.buildPath(monorepoRoutePaths.vip),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'vip', section: 'Website', categoryArg }),
          isDisabled: pagesDisabled.has(EventPageSlugs.WeddingParty),
          telemetryName: monorepoRoutePaths.vip,
        },
      ],
      [EventPageSlugs.Vip]: [
        {
          title: tq`Wedding Party`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.WeddingParty],
          icon: Guests,
          path: routeHelpers.buildPath(monorepoRoutePaths.vip),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'vip', section: 'Website', categoryArg }),
          isDisabled: pagesDisabled.has(EventPageSlugs.WeddingParty),
          telemetryName: monorepoRoutePaths.vip,
        },
      ],
      [EventPageSlugs.Registry]: [
        {
          title: tq`Registry`,
          customTitle: pageSlugToTitleMap[EventPageSlugs.Registry],
          icon: Registry,
          path: routeHelpers.buildPath(EventPageSlugs.Registry),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'registrylinks', section: 'Website', categoryArg }),
          isDisabled: pagesDisabled.has(EventPageSlugs.Registry),
          telemetryName: EventPageSlugs.Registry,
        },
      ],
      [EventPageSlugs.Custom]: (id: string, pageSlug: string, pageTitle?: string) => {
        return [
          {
            title: pageTitle ?? '',
            customTitle: pageTitle ?? '',
            icon: isThingsToDoSlug(pageSlug) ? Experiences : CustomPage,
            hasNewPill: isThingsToDoSlug(pageSlug) && !isThingsToDoPageEditorSeen,
            path: routeHelpers.buildPath(routePaths.customPage.goToPath(pageSlug)),
            onClick: (categoryArg?: string) => telemetry.openPage({ label: `website.custompage`, section: 'Website', categoryArg, pageSlug }),
            pathIsInJoyWeb: true,
            isDisabled: pagesDisabled.has(id),
            telemetryName: pageSlug,
          },
        ];
      },
    };

    eventPages.forEach(page => {
      const item = editItemsDictionary[page.pageSlug as keyof typeof editItemsDictionary];

      if (item && typeof item !== 'function' && shouldUseNativePageEntry(page, isMigratedCustomPageEnabled)) {
        editPagesSubItems = editPagesSubItems.concat(item);
      } else if (page.type === EventPageType.custom) {
        const itemCustom = editItemsDictionary[EventPageSlugs.Custom as keyof typeof editItemsDictionary];
        if (typeof itemCustom === 'function') {
          editPagesSubItems = editPagesSubItems.concat(itemCustom(page.id, page.pageSlug, page?.pageTitle));
        }
      }
    });

    const accommodationTileIndex = editPagesSubItems.findIndex(item => item.telemetryName === EventPageSlugs.Accommodations);

    return {
      title: tq`Website`,
      stringOn: tq`On`,
      stringOff: tq`Off`,
      settingsUrl: routeHelpers.buildPath(monorepoRoutePaths.settingsMenu),
      onPageVisibilityClick: () => telemetry.openPage({ label: 'PageVisibility', section: 'Website' }),
      isEnabled,
      visibility,
      children: [
        {
          id: 'website-overview',
          title: tq`Overview`,
          icon: WebGlobe,
          path:
            isAdminDashboardUpdateEnabled && !isLoadingVisitStatus && !websiteDesignerVisitStatus?.eventByName?.eventDesign?.hasVisitedDesignPage
              ? routeHelpers.buildPath(adminRoutePaths.design.path)
              : routeHelpers.buildPath(adminRoutePaths.websiteOverview.path),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'websiteOverview', section: 'Website', categoryArg }),
          pathIsInJoyWeb: true,
          telemetryName: adminRoutePaths.websiteOverview.path,
        },
        {
          id: 'website-design',
          title: tq`Design`,
          icon: Design,
          path: routeHelpers.buildPath(adminRoutePaths.design.path),
          pathIsInJoyWeb: true,
          telemetryName: adminRoutePaths.design.path,
          onClick: () => telemetry.designTabClicked(),
          ...(isGalleryV2Enabled &&
            !isWebsiteDesignerV3Enabled && {
              subItems: [
                {
                  title: tq`Templates Gallery`,
                  path: routeHelpers.buildPath(`${adminRoutePaths.design.path}?tab=${DesignConsoleRouteName.Templates}`),
                  onClick: (categoryArg?: string) => telemetry.openPage({ label: 'websiteDesigner', section: 'Templates Gallery', categoryArg }),
                  pathIsInJoyWeb: true,
                  telemetryName: `${adminRoutePaths.design.path}-templates-gallery`,
                },
                {
                  title: tq`Your Site Design`,
                  path: routeHelpers.buildPath(`${adminRoutePaths.design.path}?tab=${DesignConsoleRouteName.Layout}`),
                  onClick: (categoryArg?: string) => telemetry.openPage({ label: 'websiteDesigner', section: 'Site Design', categoryArg }),
                  pathIsInJoyWeb: true,
                  telemetryName: `${adminRoutePaths.design.path}-your-site-design`,
                },
              ],
            }),
        },
        {
          id: 'website-photos',
          title: tq`Photos`,
          icon: Photos,
          path: routeHelpers.buildPath(adminRoutePaths.photoCollection.path),
          pathIsInJoyWeb: true,
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'photos', section: 'Website', categoryArg }),
          telemetryName: adminRoutePaths.photoCollection.path,
        },
        ...(accommodationTileIndex !== -1 ? [{ id: 'website-accommodations', ...editPagesSubItems[accommodationTileIndex], hasNewPill: false }] : []),
        {
          id: 'website-editPages',
          title: tq`Edit Pages`,
          icon: EditPages,
          subItems: [
            ...editPagesSubItems,
            {
              title: tq`Add New Page`,
              icon: Add,
              onClick: (_categoryArg?: string) => {
                telemetry.addNewPageOpen();
                openAddCustomPageDialog();
                setCustomPageDialogData({ pages: [...eventPages] });
              },
              hasDivider: true,
              telemetryName: 'addNewPage',
            },
            ...(!useCarbonScopedWebsiteEditor
              ? [
                  {
                    title: tq`Manage Pages`,
                    icon: Settings,
                    path: routeHelpers.buildPath(monorepoRoutePaths.settingsMenu),
                    onClick: (categoryArg?: string) => telemetry.openPage({ label: 'managePages', section: 'Website', categoryArg }),
                    hasDivider: false,
                    telemetryName: monorepoRoutePaths.settingsMenu,
                  },
                ]
              : []),
          ],
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    eventPages,
    routeHelpers,
    telemetry,
    productVerticals,
    tq,
    isGalleryV2Enabled,
    isWebsiteDesignerV3Enabled,
    isMigratedCustomPageEnabled,
    isAdminDashboardUpdateEnabled,
    websiteDesignerVisitStatus?.eventByName?.eventDesign?.hasVisitedDesignPage,
    isLoadingVisitStatus,
  ]);

  return websiteProductRow;
};
